import { useRef, useEffect } from 'react'
import { useRouter } from 'next/router'

import { VERIFICATION_MESSAGE_TYPE, type VerificationMessage } from '../constants/nice'
import { openNiceVerificationPopup } from '../utils/verification'

import { clientIdentityVerificationService } from '@/services/account/identity-verification'
import { IdentityVerificationInitiateData } from '@/services/account/identity-verification/types'
import { getSourceFromPath } from '../utils/event'
import { postAmplitude } from '@/core/lib'

interface Props {
  onComplete: (success: boolean) => void
  onTokenReceived?: (token: string) => void
}

export const useNiceVerification = ({ onComplete, onTokenReceived }: Props) => {
  const router = useRouter()
  const popupRef = useRef<Window | null>(null)
  const niceDataRef = useRef<IdentityVerificationInitiateData | null>(null)

  useEffect(() => {
    const messageHandler = (event: MessageEvent<VerificationMessage>) => {
      if (event.origin !== window.location.origin) return

      if (event.data.type === VERIFICATION_MESSAGE_TYPE.COMPLETE) {
        onComplete(true)
        postAmplitude('identity_verification_success', {
          source: getSourceFromPath(router.pathname),
        })
        cleanup()
      } else if (event.data.type === VERIFICATION_MESSAGE_TYPE.ERROR) {
        onComplete(false)
        postAmplitude('identity_verification_failure', {
          source: getSourceFromPath(router.pathname),
          error_reason: event.data.errorMsg ?? 'unknown',
        })
        cleanup()
      }
    }

    window.addEventListener('message', messageHandler)

    return () => {
      window.removeEventListener('message', messageHandler)
    }
  }, [onComplete, router.pathname])

  // 본인인증 시작 함수
  const startVerification = async () => {
    postAmplitude('identity_verification_start', {
      source: getSourceFromPath(router.pathname),
    })
    try {
      // 1. 본인인증 초기화 API 호출
      const redirectUri = `${window.location.origin}/identity-verification/bridge`
      const result = await clientIdentityVerificationService.initiate('phone', {
        success: redirectUri,
        failure: redirectUri,
      })

      // 2. NICE 데이터 저장
      niceDataRef.current = {
        tokenVersionId: result.tokenVersionId,
        encData: result.encData,
        integrityValue: result.integrityValue,
        externalToken: result.externalToken,
      }

      // 3. 토큰 콜백 호출
      onTokenReceived?.(result.externalToken)

      // 4. 팝업 열기
      const popup = openNiceVerificationPopup(niceDataRef.current)
      if (!popup || popup.closed) {
        onComplete(false)
        return
      }

      popupRef.current = popup
    } catch {
      onComplete(false)
    }
  }

  // 팝업 및 데이터 클린업 함수
  const cleanup = () => {
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close()
      popupRef.current = null
    }

    niceDataRef.current = null
  }

  return { startVerification }
}
